import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  List,
  Checkbox,
  Divider,
  Switch,
  Link,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { formatTimeAgo } from "../../utils/dateHelper";
import { MarkMessageRead, Message, MessageRequest } from "../../types/Message";
import useApi from "../../hooks/useApi";
import { useUser } from "../../context/UserContext";
import { useSettingValue } from "../../services/useSettingValue";
import { GLOBALSETTINGS, SEVERITIES, SYSTEMSETTINGS } from "../../data/constants";
import { getNotifications, markMessageAsRead } from "../../services/messageService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useToaster } from "../../hooks/useToaster";
import { INTERNALMESSAGES, MESSAGEREAD_FAIL } from "../../constants/messages";
import SimpleProgressBar from "../../components/ui/SimpleProgressBar";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { notificationDestination } from "./notificationDestination";

interface NotificationListProps {
  onItemClick: () => void;
  onCountChange?: React.Dispatch<React.SetStateAction<number>>;
}

const NotificationList: React.FC<NotificationListProps> = ({ onItemClick, onCountChange }) => {
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.NOTIFICATONS_PAGINATION);
  const [notifications, setNotifications] = useState<Message[]>([]);
  const [unread, setUnread] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [infoMessage, setInfoMessage] = useState<Message | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);
  const { userId } = useUser();
  const showToaster = useToaster();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    const requestData: MessageRequest = {
      toUserId: userId,
      pageNumber: page,
      pageSize,
      onlyUnread: unread,
    };

    const result = await executeApi<MessageRequest, Message[]>(getNotifications, requestData);
    if (result) {
      const total = result[0]?.totalRecords || 0;
      setNotifications((prev) => {
        const existingIds = new Set(prev.map((msg) => msg.messageId));
        const newUnique = result.filter((msg) => !existingIds.has(msg.messageId));
        return [...prev, ...newUnique];
      });
      setHasMore(notifications.length + result.length < total);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize, unread]);

  useEffect(() => {
    setNotifications([]);
    setPage(1);
  }, [unread]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (
      container &&
      container.scrollTop + container.clientHeight >= container.scrollHeight - 50 &&
      hasMore &&
      !loading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const handleRedirectLink = async (e: React.MouseEvent<HTMLAnchorElement>, msg: Message) => {
    e.preventDefault();
    if (!msg.redirectLink) {
      return;
    }
    markAsRead(msg, true);
    onItemClick();
    // Query-only RedirectLink would stay on this page. Open the file explorer
    // so FolderTree can highlight the folder (Bug 82195).
    navigate(notificationDestination(msg.redirectLink));
  };

  // The info icon opens the details in place. It must NOT call onItemClick()/navigate() —
  // doing so closed this panel and routed away (and navigate(null) on a notification with no
  // redirectLink silently did nothing at all), which is why the icon appeared dead.
  const handleInfoOpen = useCallback((event: React.MouseEvent<HTMLElement>, msg: Message) => {
    setInfoAnchorEl(event.currentTarget);
    setInfoMessage(msg);
  }, []);

  const handleInfoClose = useCallback(() => {
    setInfoAnchorEl(null);
    setInfoMessage(null);
  }, []);

  const markAsRead = async (msg: Message, checked: boolean) => {
    // Without a recipientID the update matches no row server-side, so fail fast
    // instead of sending 0 and reporting a generic error after a wasted round trip.
    if (!msg.recipientID) {
      showToaster(MESSAGEREAD_FAIL(checked), SEVERITIES.ERROR);
      return;
    }
    const markMessageRead: MarkMessageRead = {
      messageId: msg.messageId,
      recipientId: msg.recipientID,
      readVia: msg.readOn,
      markedAsRead: checked,
    };
    const updateRead = await executeApi<MarkMessageRead, boolean>(
      markMessageAsRead,
      markMessageRead
    );

    if (!updateRead) {
      showToaster(MESSAGEREAD_FAIL(checked), SEVERITIES.ERROR);
    } else {
      if (onCountChange) {
        onCountChange((prev) => (checked ? prev - 1 : prev + 1));
      }
      setNotifications((prev) =>
        prev.map((m) => (m.messageId === msg.messageId ? { ...m, markedAsRead: checked } : m))
      );
    }
  };

  return (
    <Box
      width={400}
      borderRadius={2}
      boxShadow={3}
      sx={{ maxHeight: 500, overflowY: "auto", p: 2, position: "relative" }}
    >
      <Typography id="notificationLabel" variant="subtitle1" fontWeight="bold" mb={0.5}>
        Notifications
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
          }}
        >
          <SimpleProgressBar loading={true} size={100} />
        </Box>
      )}
      <Box
        onScroll={handleScroll}
        ref={containerRef}
        sx={{
          overflowY: "auto",
          maxHeight: 380,
          flexGrow: 1,
        }}
      >
        {!loading && (!notifications || notifications.length === 0) && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            No {unread ? "unread " : ""} notifications to display
          </Typography>
        )}
        <List id="notificatList">
          {notifications.map((msg) => (
            <Box
              key={msg.messageId}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 1,
                boxShadow: 0.5,
                border: "1px solid #e0e0e0",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: "0.8rem",
                  mr: 1,
                }}
              >
                {(msg.cacheFromName || "U")[0]}
              </Avatar>

              <Box flexGrow={1}>
                {msg.redirectLink ? (
                  <Link
                    href="#"
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 500, display: "block", mb: 0.25 }}
                    onClick={(e) => {
                      handleRedirectLink(e, msg);
                    }}
                  >
                    {msg.messageSubject}
                  </Link>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500, display: "block", mb: 0.25 }}>
                    {msg.messageSubject}
                  </Typography>
                )}
                <Typography variant="caption" className="text-break notification-msg">
                  {msg.description} - {msg.messageBody}
                </Typography>
                <Box display="flex" alignItems="center" mt={0.25}>
                  <Tooltip
                    title={`${msg.createdOn ? format(msg.createdOn, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""}`}
                  >
                    {msg.markedAsRead ? (
                      <ChatBubbleRoundedIcon
                        id="chatRounded"
                        sx={{
                          fontSize: 12,
                          mr: 0.5,
                          color: "success.main",
                        }}
                      />
                    ) : (
                      <ChatBubbleOutlineIcon
                        id="chatOutlined"
                        sx={{
                          fontSize: 12,
                          mr: 0.5,
                        }}
                      />
                    )}
                  </Tooltip>
                  <Tooltip
                    title={`${msg.createdOn ? format(msg.createdOn, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""}`}
                  >
                    <Typography variant="caption">{formatTimeAgo(msg.createdOn)}</Typography>
                  </Tooltip>
                </Box>
              </Box>

              {/* Each control is boxed to the exact height of the text line it sits against,
                  so the column lines up row-for-row with the content beside it: the checkbox
                  against the subject (body2 = 20px line + its 0.25 bottom margin) and the info
                  icon against the message body (caption = 20px line). Default padding would
                  inflate both boxes and break the pairing, hence p: 0. */}
              <Box display="flex" flexDirection="column" alignItems="center" ml={1}>
                <Checkbox
                  id="updateRead"
                  size="small"
                  checked={msg.markedAsRead}
                  onChange={(e) => markAsRead(msg, e.target.checked)}
                  sx={{ p: 0, height: 22 }}
                />
                <Tooltip title={INTERNALMESSAGES.VIEW_NOTIFICATION_DETAILS}>
                  <IconButton
                    size="small"
                    aria-label={INTERNALMESSAGES.VIEW_NOTIFICATION_DETAILS}
                    onClick={(e) => handleInfoOpen(e, msg)}
                    sx={{ p: 0, height: 20 }}
                  >
                    <InfoOutlinedIcon
                      id="infoRead"
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </List>
      </Box>

      <Divider sx={{ mb: 1 }} />
      <Box mt={1} display="flex" alignItems="center">
        <Switch
          id="switchUnread"
          size="small"
          checked={unread}
          onChange={(e) => setUnread(e.target.checked)}
        />
        <Typography variant="body2" ml={1}>
          Unread
        </Typography>
      </Box>

      {/* Details for the ⓘ icon. Nested inside the bell Popover on purpose: MUI stacks the
          modals, so dismissing this one does not also close the notification panel. */}
      <Popover
        id="notificationInfoPopover"
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 1.5, maxWidth: 280 }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
            {INTERNALMESSAGES.NOTIFICATION_DETAILS}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {/* The colon sits in its own grid column so it lines up vertically across rows;
              column 1 sizes to the widest label, which keeps the values aligned too. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "max-content max-content 1fr",
              columnGap: 0.5,
              rowGap: 0.25,
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {INTERNALMESSAGES.SENDER_NAME}
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              :
            </Typography>
            <Typography id="infoSenderName" variant="caption" className="text-break">
              {infoMessage?.cacheFromName}
            </Typography>

            <Typography variant="caption" fontWeight="bold">
              {INTERNALMESSAGES.DATE_TIME}
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              :
            </Typography>
            <Typography id="infoDateTime" variant="caption">
              {infoMessage?.createdOn
                ? format(infoMessage.createdOn, GLOBALSETTINGS.DATE_FORMAT_TIME)
                : ""}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default NotificationList;
